import axios, {
	AxiosError,
	AxiosRequestConfig,
	AxiosResponse,
	InternalAxiosRequestConfig,
} from "axios";
import _ from "lodash";
import { toast } from "react-toastify";
import { IError, ISuccessResponse } from "./types";
import { redirectToLogin } from "./user/logout";

// for multiple requests
let isRefreshing = false;
let failedQueue: { resolve: (value: unknown) => void; reject: (reason?: any) => void }[] = [];

const processQueue = (error: any, token: any = undefined) => {
	failedQueue.forEach((prom) => {
		if (error) {
			prom.reject(error);
		} else {
			prom.resolve(token);
		}
	});

	failedQueue = [];
};

export const axiosInstance = axios.create({
	baseURL: process.env.WEB_API_URL,
	withCredentials: true,
});

export const baseAxios = axios.create({
	baseURL: process.env.WEB_API_URL,
	withCredentials: true,
});

const handleSuccess = (res: AxiosResponse<ISuccessResponse>) => {
	if (res.data?.code < 200 && res.data?.code > 299) {
		return Promise.reject(res.data);
	}

	const result = res.data;
	if (result?.pagination) {
		result.data = {
			list: result.data,
			pagination: result?.pagination,
		};
	}
	return result;
};

const handleBaseError = (error: AxiosError<IError>) => {
	const data = error?.response?.data;
	if (typeof data?.message === "string") {
		data.message = [data.message];
	}
	return Promise.reject(data);
};

const handleError = async (error: AxiosError<IError>) => {
	// axios 1.x types `error.config` as InternalAxiosRequestConfig | undefined.
	// At runtime it is always present for response-phase errors; assert here so
	// the downstream _.set / axiosInstance(originalRequest) calls stay clean.
	const originalRequest = error.config as InternalAxiosRequestConfig;
	const isLoginSessionProbe =
		window.location.pathname === "/investors/login" &&
		originalRequest.method?.toLowerCase() === "get" &&
		originalRequest.url === "/investors/profile";

	// Login deliberately probes the current session before deciding whether to
	// continue to Trading. A missing session is expected there; let the page
	// handle the 401 instead of sending it through the logout redirect flow.
	if (isLoginSessionProbe && error?.response?.status === 401) {
		return Promise.reject(error?.response?.data);
	}

	// Check if it's a 401 error and handle both JSON and ArrayBuffer responses
	if (error?.response?.status === 401 && !(originalRequest as any)._retry) {
		// For arraybuffer responses, we need to check the response type differently
		let isTokenExpired = false;

		if (originalRequest.responseType === "arraybuffer") {
			// Try to parse arraybuffer as JSON to check for TOKEN_EXPIRED
			try {
				const uint8Array = new Uint8Array(error.response?.data as unknown as ArrayBuffer);
				const jsonString = new TextDecoder().decode(uint8Array);
				const responseObject = JSON.parse(jsonString);
				isTokenExpired = responseObject?.type === "TOKEN_EXPIRED";
			} catch (e) {
				// If parsing fails, assume it's a token expired error for 401 with arraybuffer
				isTokenExpired = true;
			}
		} else {
			// For regular JSON responses
			isTokenExpired = error?.response?.data?.type === "TOKEN_EXPIRED";
		}

		if (isTokenExpired) {
			if (isRefreshing) {
				return new Promise(function (resolve, reject) {
					failedQueue.push({ resolve, reject });
				})
					.then(async (token) => {
						if (typeof originalRequest.data === "string") {
							_.set(originalRequest, "data", JSON.parse(originalRequest.data));
						}
						return axiosInstance(originalRequest);
					})
					.catch((err) => {
						return Promise.reject(err);
					});
			}
			(originalRequest as any)._retry = true;
			isRefreshing = true;
			return new Promise(function (resolve, reject) {
				axios
					.post(`${process.env.WEB_API_URL}/users/token/refresh`, {
					}, { withCredentials: true })
					.then((res) => {
						if (typeof originalRequest.data === "string") {
							_.set(originalRequest, "data", JSON.parse(originalRequest.data));
						}
						processQueue(null);
						resolve(axiosInstance(originalRequest));
					})
					.catch(async (error) => {
						processQueue(error, null);

						toast.success("Session expired!", {
							autoClose: 1000,
						});
						redirectToLogin(
							`${window.location.pathname}${window.location.search}`,
						);

						reject(error);
					})
					.finally(() => {
						isRefreshing = false;
					});
			});
		}
	}

	if (error?.response?.data?.type === "UNAUTHORIZED") {
		toast.success("Session expired!", {
			autoClose: 1000,
		});
		redirectToLogin(`${window.location.pathname}${window.location.search}`);
	}

	const data = error?.response?.data;
	if (typeof data?.message === "string") {
		data.message = [data.message];
	}
	return Promise.reject(data);
};

// axios 1.x: response interceptor's onFulfilled is statically typed to return
// AxiosResponse<T>, but handleSuccess unwraps `.data`. Runtime behaviour is
// unchanged — cast to satisfy the new types.
axiosInstance.interceptors.response.use(handleSuccess as any, handleError);
baseAxios.interceptors.response.use(handleSuccess as any, handleBaseError);

baseAxios.interceptors.request.use(
	async (config: InternalAxiosRequestConfig) => config,
	(error) => Promise.reject(error),
);

export const convertToFormData = (data: { string: string }) => {
	const bodyFormData = new FormData();
	if (data) {
		for (const [key, value] of Object.entries(data)) {
			bodyFormData.append(key, value || "");
		}
	}
	return bodyFormData;
};
